import React, { createContext, useContext, useState, useCallback } from 'react'
import Loader from '@/components/ui_custom/loader'

type LoaderSize = 'sm' | 'md' | 'lg'
type LoaderVariant = 'spinner' | 'dots' | 'pulse'

interface LoaderContextType {
  isLoading: boolean
  variant: LoaderVariant
  size: LoaderSize
  color: string
  showLoader: (options?: { variant?: LoaderVariant; size?: LoaderSize; color?: string }) => void
  hideLoader: () => void
  setVariant: (variant: LoaderVariant) => void
  setSize: (size: LoaderSize) => void
  setColor: (color: string) => void
}

// @ts-expect-error
const LoaderContext = createContext<LoaderContextType>()

export const LoaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [variant, setVariant] = useState<LoaderVariant>('spinner')
  const [size, setSize] = useState<LoaderSize>('lg')
  const [color, setColor] = useState('#ffffff')

  const showLoader = useCallback((options?: { variant?: LoaderVariant; size?: LoaderSize; color?: string }) => {
    if (options?.variant) setVariant(options.variant)
    if (options?.size) setSize(options.size)
    if (options?.color) setColor(options.color)
    setIsLoading(true)
  }, [])

  const hideLoader = useCallback(() => {
    setIsLoading(false)
  }, [])

  return (
    <LoaderContext.Provider
      value={{
        isLoading,
        variant,
        size,
        color,
        showLoader,
        hideLoader,
        setVariant,
        setSize,
        setColor
      }}
    >
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-primary/60 bg-opacity-10 p-6 rounded-lg shadow-xl">
            <Loader variant={variant} size={size} color={color} />
          </div>
        </div>
      )}
      {children}
    </LoaderContext.Provider>
  )
}

export const useLoader = () => {
  const context = useContext(LoaderContext)
  if (context === undefined) {
    throw new Error('useLoader must be used within a LoaderProvider')
  }
  return context
}
